import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/header.css';
import { Menu, X, ChevronDown } from 'lucide-react';
// Import the MHL logo - adjust the path as needed
import vipersLogo from '../assets/WhiteVipers.png';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const handleNavigation = (path: string) => {
    setIsMobileMenuOpen(false);
    navigate(path);
    // Scroll to top when navigation occurs
    window.scrollTo(0, 0);
  };

  // Add effect to scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/tickets', label: 'Tickets' },
    { path: '/schedule', label: 'Schedule' },
    { path: '/merchandise', label: 'Merch' },
    { path: '/sponsor', label: 'Sponsors' },
    // { path: '/alumni', label: 'Alumni' }, // 🚧 Under construction
    { path: '/contact', label: 'Contact' },
  ];

  const teamDropdownItems = [
    { path: '/team', label: 'Team' },
    { path: '/stats', label: 'Stats' },
  ];

  // Admin pages have their own AdminNavigation. Rendering the public header there
  // stacks two headers (and two hamburgers overlap on mobile), so suppress it.
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const teamActive = teamDropdownItems.some((item) => isActive(item.path));

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link
          to="/"
          className="header-logo"
          aria-label="Breckenridge Vipers home"
          onClick={() => window.scrollTo(0, 0)}
        >
          <img src={vipersLogo} alt="" className="vipers-logo" />
          <span className="logo-vipers">
            <span className="logo-vipers__city">Breckenridge</span>
            <span className="logo-vipers__name">Vipers</span>
          </span>
        </Link>

        <nav className="header-nav desktop" aria-label="Primary">
          {navItems.slice(0, 1).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
              aria-current={isActive(item.path) ? 'page' : undefined}
              onClick={() => window.scrollTo(0, 0)}
            >
              {item.label}
            </Link>
          ))}

          {/* Team Dropdown */}
          <div
            className="site-nav-dropdown"
            onMouseEnter={() => setIsTeamDropdownOpen(true)}
            onMouseLeave={() => setIsTeamDropdownOpen(false)}
          >
            <button
              type="button"
              className={`nav-link site-dropdown-trigger ${teamActive ? 'active' : ''}`}
              aria-expanded={isTeamDropdownOpen}
              aria-haspopup="true"
              onClick={() => setIsTeamDropdownOpen((open) => !open)}
            >
              Team
              <ChevronDown size={15} className={`site-dropdown-icon ${isTeamDropdownOpen ? 'open' : ''}`} />
            </button>
            {isTeamDropdownOpen && (
              <div className="site-dropdown-menu">
                {teamDropdownItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`site-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      setIsTeamDropdownOpen(false);
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navItems.slice(1).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
              aria-current={isActive(item.path) ? 'page' : undefined}
              onClick={() => window.scrollTo(0, 0)}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <Link
            to="/tickets"
            className="vb-btn vb-btn--primary vb-btn--sm header-cta"
            onClick={() => window.scrollTo(0, 0)}
          >
            Get Tickets
          </Link>
          <button
            type="button"
            className="menu-toggle"
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={toggleMobileMenu}></div>
          <div className="mobile-menu">
            <nav className="mobile-nav" aria-label="Mobile">
              {navItems.slice(0, 1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.label}
                </Link>
              ))}

              {/* Team Section in Mobile */}
              <div className="mobile-nav-section">
                <span className="mobile-nav-header">Team</span>
                {teamDropdownItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-nav-link mobile-nav-sublink ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => handleNavigation(item.path)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {navItems.slice(1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link
              to="/tickets"
              className="vb-btn vb-btn--primary vb-btn--lg vb-btn--block mobile-menu-cta"
              onClick={() => handleNavigation('/tickets')}
            >
              Get Tickets
            </Link>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;